import { useState } from 'react';
import { BiPurchaseTag } from 'react-icons/bi';

import { buildEventPatch } from '@/store/events/eventPatch';
import type { EventResponse } from '@/store/events/events.dto';
import { eventTags } from '@/store/events/events.mappers';
import { useEvents } from '@/store/events/events.store';
import { toast } from '@/utils/toast';

import { TagPicker } from '../TagPicker/TagPicker';

import s from './EventSidePanel.module.scss';

type TagsSectionProps = {
  event: EventResponse;
  canEdit: boolean;
};

/**
 * The event's tags as chips. They are picked at the end of the create form (the AI
 * suggests them there); whoever can act on events removes, adds or creates them
 * here, and from then on the AI leaves the event's tags alone.
 */
export const TagsSection = ({ event, canEdit }: TagsSectionProps) => {
  const updateEvent = useEvents((st) => st.updateEvent);
  const getEventById = useEvents((st) => st.getEventById);
  const [isSaving, setIsSaving] = useState(false);

  const tags = eventTags(event);
  if (tags.length === 0 && !canEdit) return null;

  const saveTags = async (tagIds: string[]) => {
    setIsSaving(true);
    try {
      await updateEvent(buildEventPatch(event, { tag_ids: tagIds }) as unknown as EventResponse);
      await getEventById(event.id);
    } catch (error) {
      console.error('Event tags update error:', error);
      toast.error('Couldn’t update the tags. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className={s.section} aria-label="Tags">
      <div className={s.sectionHead}>
        <span className={s.sectionTitle}>
          <BiPurchaseTag aria-hidden="true" /> Tags
        </span>
      </div>

      <TagPicker
        value={tags.map((tag) => tag.id)}
        known={tags}
        canEdit={canEdit}
        disabled={isSaving}
        onChange={(tagIds) => void saveTags(tagIds)}
      />
    </section>
  );
};
